import {Button, Card, Layout, Modal, Text} from '@ui-kitten/components';
import React, {useState} from 'react';
import {Platform, StyleSheet} from 'react-native';
import {getCurrentPurchaseMonth, postCheckPurchase} from '../../../../apis';
import {
  isLandscape as isLandscapeUtil,
  px,
  useAsync,
  useAsyncFn,
  wp,
} from '../../../../utils';
import {
  AppLoadingIndicator,
  LoadingButton,
  TitleRow,
} from '../../../components';
import ConfirmPurchaseModal from '../../../Payment/ConfirmPurchaseModal';

const ConfigurePackageModal = ({
  kidId,
  packageData,
  onClose,
  onNextPressed,
}) => {
  const [selectedMonth, setSelectedMonth] = useState();
  const [isMonthSelected, setIsMonthSelected] = useState(false);
  const [errorText, setErrorText] = useState('');

  const [isFreeMonth, setIsFreeMonth] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const {loading: availableMonthLoading, value: availableMonth} = useAsync(
    async () => {
      try {
        return await getCurrentPurchaseMonth();
      } catch (error) {
        setErrorText(String(error));
      }
    },
  );

  const [{loading: checkPurchaseLoading}, handleCheckPurchase] = useAsyncFn(
    async (monthNumber) => {
      setSelectedMonth(undefined);
      setIsMonthSelected(false);
      setErrorText('');
      try {
        await postCheckPurchase({kidId, month: monthNumber});
        setSelectedMonth(monthNumber);
        setIsMonthSelected(true);
        if (Platform.OS !== 'ios' && monthNumber === '02-2023') {
          setIsFreeMonth(true);
          setSuccessMessage({
            title: 'Happy News!',
            text: 'The subscription for February is 100% Free.',
          });
        }
      } catch (error) {
        setErrorText(String(error));
      }
    },
    [kidId, availableMonth],
  );

  const handleNext = () => {
    const paramsToSet = {data: {...packageData, monthNumber: selectedMonth}};
    onClose();
    onNextPressed(paramsToSet);
  };

  const onModalClose = () => {
    setIsFreeMonth(false);
  };

  const renderContent = () => {
    if (availableMonthLoading) {
      return <AppLoadingIndicator />;
    }

    return (
      <>
        <Text style={styles.text} category="h6">
          {availableMonth?.message}
        </Text>
        {availableMonth?.data && availableMonth?.data.length > 0 && (
          <>
            <Text style={styles.primaryText}>
              {'Select subscription month'}
            </Text>
            {Boolean(availableMonth.data[0]?.message) && (
              <Layout style={styles.infoText}>
                <Text>{availableMonth.data[0]?.message}</Text>
              </Layout>
            )}
            {Boolean(availableMonth.data[1]?.message) && (
              <Layout style={styles.infoText}>
                <Text>{availableMonth.data[1]?.message}</Text>
              </Layout>
            )}
            <Layout style={styles.rowContainer}>
              <LoadingButton
                loading={checkPurchaseLoading}
                disabled={!availableMonth.data[0]?.isActive}
                style={{width: px(120)}}
                appearance={
                  selectedMonth === availableMonth.data[0]?.month_number
                    ? 'filled'
                    : 'outline'
                }
                onPress={() =>
                  handleCheckPurchase(availableMonth.data[0]?.month_number)
                }>
                {availableMonth.data[0]?.month_name}
              </LoadingButton>
              <LoadingButton
                loading={checkPurchaseLoading}
                disabled={!availableMonth.data[1]?.isActive}
                style={{width: px(120)}}
                appearance={
                  selectedMonth === availableMonth.data[1]?.month_number
                    ? 'filled'
                    : 'outline'
                }
                onPress={() =>
                  handleCheckPurchase(availableMonth.data[1]?.month_number)
                }>
                {availableMonth.data[1]?.month_name}
              </LoadingButton>
            </Layout>
          </>
        )}
        {Boolean(errorText) && (
          <Layout style={styles.errorText}>
            <Text status="danger">{errorText}</Text>
          </Layout>
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        visible={Boolean(packageData)}
        backdropStyle={styles.backdrop}
        onBackdropPress={() => onClose()}>
        <Card disabled={true}>
          <Layout style={styles.container}>
            <TitleRow
              title="Select Month"
              type="primary"
              iconName="close-circle-outline"
              onIconClick={() => onClose()}
              containerStyle={styles.title}
            />
            {renderContent()}
            <Button
              disabled={!isMonthSelected}
              onPress={handleNext}
              style={styles.submitButton}>
              {'Next'}
            </Button>
          </Layout>
        </Card>
      </Modal>
      {isFreeMonth && (
        <ConfirmPurchaseModal
          title={successMessage?.title || 'Oops'}
          text={successMessage?.text || 'Something went wrong'}
          onClose={() => onModalClose()}
          visible={true}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    width: isLandscapeUtil() ? wp(30) : wp(90),
  },
  backdrop: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  title: {
    width: '100%',
  },
  primaryText: {
    fontWeight: '600',
    fontSize: 16,
    textAlign: 'center',
    marginTop: px(24),
  },
  text: {
    fontWeight: '400',
    textAlign: 'center',
    marginHorizontal: px(17),
    marginBottom: px(17),
  },
  input: {
    marginHorizontal: px(17),
    marginBottom: px(17),
  },
  submitButton: {
    marginHorizontal: px(17),
    marginTop: px(26),
    marginBottom: px(17),
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    margin: px(17),
    marginTop: px(28),
  },
  infoText: {
    marginHorizontal: px(10),
    paddingVertical: px(10),
    textAlign: 'center',
    marginTop: px(16),
  },
  errorText: {
    marginHorizontal: px(17),
    borderColor: 'red',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    paddingHorizontal: px(15),
    paddingVertical: px(10),
    textAlign: 'center',
    marginBottom: px(40),
  },
});

export default ConfigurePackageModal;
